import MultiSelectInput from '@repo/ui/multiselect-input'
import { useQuery } from '@tanstack/react-query'

import { getFormulas } from '../../../../api/formula/get-formulas'
import { getPeriods } from '../../../../api/periods/get-periods'
import useTranslate from '../../../../lib/hooks/use-translate'
import { OlTeflPriceFilters } from '../../../../lib/types/models/ol/tefl-price/ol-tefl-price'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { t } from '../../../../locale/utils'
import { getLocalizedLabel } from '../../../../utils/functions/get-localized-label'

interface Props {
  filter?: OlTeflPriceFilters
  onFilterChange: (filter: OlTeflPriceFilters) => void
}

export function OlTeflPricesFilter({ filter, onFilterChange }: Props) {
  const { currentLanguage } = useTranslate()
  function setFilter<K extends keyof OlTeflPriceFilters>(key: K, value: OlTeflPriceFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />
      <MultiSelectInput
        searchable
        label={t('commissions.filters.formulas')}
        variant={'primary'}
        options={formulas?.data.map((formula) => ({
          label: getTranslatedName(formula, currentLanguage) || '—',
          value: formula.id
        }))}
        selectedOptions={filter?.formulas?.map((formulaId) => {
          const selectedFormula = formulas?.data.find((f) => f.id === formulaId)
          if (!selectedFormula) return { label: formulaId.toString(), value: formulaId }
          return {
            label: getTranslatedName(selectedFormula, currentLanguage) || '—',
            value: formulaId
          }
        })}
        onChange={(formulas) => setFilter('formulas', formulas)}
      />

      <MultiSelectInput
        searchable
        label={t('commissions.filters.periods')}
        variant={'primary'}
        options={periods?.data.map((period) => ({
          label: getLocalizedLabel(period, currentLanguage, {
            fr: 'name',
            en: 'nameEn'
          }),
          value: period.name
        }))}
        selectedOptions={filter?.periods?.map((period) => {
          const selectedPeriod = periods?.data.find((p) => p.name === period)
          if (!selectedPeriod) return { label: period, value: period }
          return {
            label: getLocalizedLabel(selectedPeriod, currentLanguage, {
              fr: 'name',
              en: 'nameEn'
            }),
            value: period
          }
        })}
        onChange={(periods) => setFilter('periods', periods)}
      />
    </div>
  )
}
